import React, { FC, memo, ReactNode } from 'react';
import { useMutation } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import {
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons/lib';
import { Button, message, Modal, Tooltip } from 'antd';
import { ProfileOwner } from 'models/user';
import { ButtonProps } from 'antd/lib/button';
import { DeleteUserStatus } from 'models/deleteUserStatus';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

export interface BusinessPartnerDeleteProfileOwnerButtonProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  businessPartnerId: number;
  profileOwner: ProfileOwner;
  status?: DeleteUserStatus;
  tooltip?: ReactNode;
  onDeleted?: () => void;
}

export const BusinessPartnerDeleteProfileOwnerButton: FC<BusinessPartnerDeleteProfileOwnerButtonProps> = memo(
  ({
    businessPartnerId,
    profileOwner,
    status = DeleteUserStatus.Archive,
    onDeleted,
    tooltip = 'Archive user',
    ...buttonProps
  }) => {
    const [mutate, { isLoading }] = useMutation(BusinessPartnersAPI.deleteUser);
    const intlMessage = useFormatMessage();

    const confirm = () =>
      Modal.confirm({
        title:
          DeleteUserStatus.Delete === status
            ? intlMessage(
                { id: 'entity.delete.confirmation' },
                {
                  name: `${profileOwner.firstName} ${profileOwner.lastName}`,
                }
              )
            : intlMessage(
                { id: 'entity.archive.confirmation' },
                {
                  name: `${profileOwner.firstName} ${profileOwner.lastName}`,
                }
              ),
        icon: <ExclamationCircleOutlined />,
        okText: intlMessage({ id: 'yes' }),
        cancelText: intlMessage({ id: 'cancel' }),
        okType: 'danger',
        onOk: () =>
          mutate(
            {
              businessPartnerId,
              dto: { userId: profileOwner.userId!, status },
            },
            {
              onSuccess: () => {
                message.success(
                  DeleteUserStatus.Delete === status
                    ? intlMessage(
                        { id: 'entity.delete.success' },
                        {
                          name: `${profileOwner.firstName} ${profileOwner.lastName}`,
                        }
                      )
                    : intlMessage(
                        { id: 'entity.archive.success' },
                        {
                          name: `${profileOwner.firstName} ${profileOwner.lastName}`,
                        }
                      )
                );
                onDeleted && onDeleted();
              },
            }
          ),
      });

    const button = (
      <Button
        id="button-delete-bp-profile-owner"
        loading={isLoading}
        onClick={() => confirm()}
        icon={<WarningOutlined />}
        {...buttonProps}
      />
    );

    return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
  }
);
